import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import { routes } from "constants/routes";
import HistoryPaymentItem from "features/main/HaulPay/modules/Carrier/HistoryPaymentItem";
import { QUERY_CARRIER_FUNDING_REQUEST } from "features/main/HaulPay/Shared/EditFundingRequest/Manage/query";
import { formatNumber, formatAccounting } from "helpers/formatNumber";
import { IFRAttachment, IFactoringProfile, InformationFormData } from "models/HaulPay";
import { calculateFRAPI } from "services/api/haulpay";
import { selectFactoringProfile } from "store/user/selector";
import styles from "./style.module.scss";
import dollarsToCents from "helpers/dollarsToCents";

interface IProps {
  informationFormData?: InformationFormData;
  handleNext: VoidFunction;
  createdFR: any;
}

export default function PayConfirmation({
  informationFormData,
  handleNext,
  createdFR
}: Readonly<IProps>) {
  const navigate = useNavigate();
  const [totalToBePaid, setTotalToBePaid] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<IFRAttachment[]>([]);
  const [yourRate, setYourRate] = useState("");
  const [clientAdvanceFee, setClientAdvanceFee] = useState(0);
  const factoringProfile: IFactoringProfile = useSelector(selectFactoringProfile);

  const { data } = useQuery(QUERY_CARRIER_FUNDING_REQUEST, {
    variables: {
      id: createdFR.id
    },
    fetchPolicy: "cache-and-network"
  });

  const goToFrDetails = () => {
    navigate(`${routes.HAULPAY_CARRIER_FUNDING_REQUEST}/${createdFR.id}`);
  };

  useEffect(() => {
    if (informationFormData) {
      calculateFRAPI({
        debtor: informationFormData.billToCompany?.id ?? "",
        factored: !informationFormData.isNonFactoredInvoice,
        invoice_amount: dollarsToCents(informationFormData.totalInvoiceAmount),
        create_rates_revision: false,
        payment_profile_amount: 0,
        factoring: factoringProfile?.id,
        funding_request_id: createdFR.id,
        contract_type: informationFormData.isNonFactoredInvoice
          ? "NON_FACTORED_BILL_OUT"
          : "STD_CARRIER",
        discount_rate_comfreight: informationFormData.discountRate
      }).then((response) => {
        const factoringPayable = (response.data.factoring_payable ?? 0) / 100;
        const apiAdvanceFee =
          (response.data.factored && response.data.client_advance_fee > 0
            ? response.data.client_advance_fee
            : response.data.factoring_fee ?? 0) / 100;

        setTotalToBePaid(factoringPayable);
        setClientAdvanceFee(apiAdvanceFee);
        const hasClientAdvanceFee = response.data?.client_advance_fee != null;
        const discountRateDisplay = hasClientAdvanceFee
          ? (response.data.discount_rate_comfreight * 100).toFixed(2)
          : response.data.discount_rate_comfreight.toFixed(2);
        setYourRate(`${discountRateDisplay}%`);
      });
    }
  }, [informationFormData, factoringProfile?.id]);

  useEffect(() => {
    if (data?.fundingRequest) {
      setUploadedFiles(data.fundingRequest.attachments as IFRAttachment[]);
    }
  }, [data]);

  return (
    <div className={styles.payConfirmationContainer}>
      <div className={styles.body}>
        <h4 className={styles.header}>Confirmation</h4>

        <div className={styles.inputField}>
          <Input
            label="&nbsp;&nbsp;Total Invoice Amount"
            defaultValue={
              informationFormData && formatNumber(informationFormData.totalInvoiceAmount, 2)
            }
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Load Number"
            defaultValue={informationFormData?.loadNumber}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Bill To Company"
            defaultValue={informationFormData?.billToCompany?.dba}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Is this a non-factored invoice?"
            defaultValue={informationFormData?.isNonFactoredInvoice ? "Yes" : "No"}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Any fuel advance?"
            defaultValue={informationFormData?.isFuelAdvance ? "Yes" : "No"}
            disabled
          />
          <Select
            label="&nbsp;&nbsp;Payment Method"
            options={informationFormData?.paymentMethod ? [informationFormData?.paymentMethod] : []}
            value={informationFormData?.paymentMethod}
            disabled
          />
        </div>

        {uploadedFiles.length ? (
          <div className={styles.uploadFileList}>
            {uploadedFiles.map((item) => (
              <HistoryPaymentItem
                key={item.id}
                label={item.filename}
                category={
                  typeof item.category === "string" ? item.category : item.category.join(",")
                }
                data={{ preview_url: item.downloadUrl } as any}
              />
            ))}
          </div>
        ) : null}

        <div className={styles.totalPayment}>
          {factoringProfile.minimum_fee / 100 !== clientAdvanceFee &&
            !informationFormData?.isNonFactoredInvoice && (
              <div className="flex justify-between">
                <span className={styles.rate}>{`Your Rate: ${yourRate}`}</span>
              </div>
            )}
          <div className="flex justify-between">
            <span className={styles.rate}>{`Fees: ${formatAccounting(-clientAdvanceFee, 2)}`}</span>
          </div>
          <div className="flex justify-between">
            <div className="text-text">
              <span className="font-medium desktop:text-xl">Total </span>
              <span className="text-sm desktop:text-base">(To be Paid)</span>
            </div>
            <span className={styles.totalAmount}>{formatAccounting(totalToBePaid, 2)}</span>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="outline" onClick={goToFrDetails}>
          Edit
        </Button>
        <Button onClick={handleNext}>Confirm</Button>
      </div>
    </div>
  );
}
